import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Servicio para manejar operaciones de Colaboradores
 * Encapsula todas las llamadas al API del módulo de RRHH
 */
class ColaboradoresService {
    /**
     * Configurar headers de autenticación
     */
    _getHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }

    /**
     * Configurar headers para multipart/form-data
     */
    _getMultipartHeaders(token) {
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        };
    }

    /**
     * Manejar errores de API
     */
    _handleError(error, defaultMessage) {
        if (error.response) {
            throw new Error(error.response.data.message || defaultMessage);
        } else if (error.request) {
            throw new Error('No se pudo conectar con el servidor');
        } else {
            throw new Error(error.message || defaultMessage);
        }
    }

    /**
     * Obtener colaboradores con filtros opcionales
     *
     * @param {Object} params - Parámetros de filtro
     * @param {number} params.id_company - ID de la empresa (requerido)
     * @param {string} params.estado - Estado del colaborador (opcional)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Array>} Lista de colaboradores
     */
    async getColaboradores(params, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/colaboradores`,
                {
                    params,
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data || [];
        } catch (error) {
            this._handleError(error, 'Error al obtener colaboradores');
        }
    }

    /**
     * Obtener un colaborador por ID
     *
     * @param {number} id - ID del colaborador
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Datos del colaborador
     */
    async getColaboradorById(id, id_company, token) {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/colaboradores/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al obtener colaborador');
        }
    }

    /**
     * Crear un nuevo colaborador
     *
     * @param {Object} colaboradorData - Datos del colaborador
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Colaborador creado
     */
    async createColaborador(colaboradorData, token) {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/colaboradores`,
                colaboradorData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al crear colaborador');
        }
    }

    /**
     * Actualizar un colaborador existente
     *
     * @param {number} id - ID del colaborador
     * @param {Object} colaboradorData - Datos actualizados del colaborador
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Colaborador actualizado
     */
    async updateColaborador(id, colaboradorData, token) {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/api/rrhh/colaboradores/${id}`,
                colaboradorData,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al actualizar colaborador');
        }
    }

    /**
     * Cambiar estado de un colaborador
     *
     * @param {number} id - ID del colaborador
     * @param {Object} data - Datos del cambio de estado
     * @param {number} data.id_company - ID de la empresa
     * @param {string} data.estado - Nuevo estado (ACTIVO, INACTIVO, SUSPENDIDO)
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Colaborador actualizado
     */
    async cambiarEstado(id, data, token) {
        try {
            const response = await axios.patch(
                `${API_BASE_URL}/api/rrhh/colaboradores/${id}/estado`,
                data,
                { headers: this._getHeaders(token) }
            );
            return response.data.data;
        } catch (error) {
            this._handleError(error, 'Error al cambiar estado del colaborador');
        }
    }

    /**
     * Eliminar un colaborador y todos sus registros relacionados
     *
     * @param {number} id - ID del colaborador
     * @param {number} id_company - ID de la empresa
     * @param {string} token - Token de autenticación
     * @returns {Promise<Object>} Resultado de la eliminación
     */
    async deleteColaborador(id, id_company, token) {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/rrhh/colaboradores/${id}`,
                {
                    params: { id_company },
                    headers: this._getHeaders(token)
                }
            );
            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al eliminar colaborador');
        }
    }

    // ============================================
    // EXTRACCIÓN DE DATOS CON IA
    // ============================================

    /**
     * Extraer datos de colaborador desde PDF usando Claude AI
     *
     * Este método usa inteligencia artificial para extraer automáticamente
     * información de documentos laborales (CV, contratos, etc.) en formato PDF,
     * facilitando la carga rápida de nuevos colaboradores.
     *
     * @param {File} pdfFile - Archivo PDF del documento laboral
     * @param {string} token - Token JWT de autenticación
     * @returns {Promise<Object>} Objeto con los datos extraídos
     *
     * Estructura de retorno:
     * {
     *   success: boolean,
     *   data: {
     *     rut: string,
     *     nombres: string,
     *     apellido_paterno: string,
     *     ... (todos los campos del colaborador)
     *   },
     *   fieldsExtracted: string[],  // Campos que se pudieron extraer
     *   fieldsMissing: string[],    // Campos que NO se pudieron extraer
     *   message: string
     * }
     *
     * @example
     * const file = document.getElementById('pdfInput').files[0];
     * const result = await extractFromPdf(file, token);
     * if (result.success) {
     *   console.log('Datos extraídos:', result.data);
     *   console.log('Campos encontrados:', result.fieldsExtracted);
     *   console.log('Campos faltantes:', result.fieldsMissing);
     * }
     */
    async extractFromPdf(pdfFile, id_company, token) {
        try {
            // Crear FormData para enviar el archivo
            const formData = new FormData();
            formData.append('pdf', pdfFile);
            formData.append('id_company', id_company);

            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/colaboradores/extract-from-pdf`,
                formData,
                { headers: this._getMultipartHeaders(token) }
            );

            return response.data;
        } catch (error) {
            this._handleError(error, 'Error al extraer datos del PDF');
        }
    }
}

export default new ColaboradoresService();
